import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import Config from "../Config";

export interface CustomerPreferences {
  language: string;
  country: string;
  currency: string;
  notificationsEnabled: boolean;
}

export interface Address {
  type: "shipping" | "billing";
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface WishlistItem {
  productId: string;
  productName: string;
  addedDate: string;
}

export interface PaymentMethod {
  type: "card" | "upi" | "netbanking";
  lastFourDigits?: string;
  expiryMonth?: string;
  expiryYear?: string;
}

export interface CustomerDetails {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  profilePic: string;
  status: string;
  preferences: CustomerPreferences;
  addresses: Address[];
  wishlist?: WishlistItem[];
  paymentMethods?: PaymentMethod[];
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
  countryDialCode: string;
  loginMethod: string;
}

export interface CustomerListResponse {
  data: CustomerDetails[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
}

export interface CustomerListParams {
  page: number;
  pageSize: number;
}

export const CustomerService = createApi({
  reducerPath: "CustomersApi",
  baseQuery: RtkBaseQueryWithReauth(Config.BASE_URL),
  tagTypes: ["Customers"],
  endpoints: (builder) => ({
    getCustomers: builder.query<CustomerListResponse, CustomerListParams>({
      query: (params) => ({
        url: `${ApiPath.allCustomers}`,
        method: "POST",
        body: {
          sortBy: "createdAt",
          sortOrder: "desc",
          page: params.page,
          pageSize: params.pageSize,
        },
      }),
      providesTags: ["Customers"],
    }),
    getCustomerDetails: builder.query<CustomerDetails, string>({
      query: (id) => ({
        url: `${ApiPath.customerById}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: CustomerDetails }) => response.data,
      providesTags: ["Customers"],
    }),

    setNewPassword: builder.mutation<any, { newPassword: string }>({
      query: (body) => ({
        url: `${ApiPath.setNewPassword}`,
        method: "POST",
        body: body,
      }),
    }),

  }),
});

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useGetCustomerDetailsQuery,
  useSetNewPasswordMutation,
} = CustomerService;
